import {
    Component,
    OnDestroy,
    TemplateRef,
    ViewChild,
    Injector,
    Inject,
    Output,
    EventEmitter,
    Input,
    OnInit
} from '@angular/core';

import { Subscription, Observable, filter, map } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';

import { TabComponent } from '../../../Layout/components/Tab/tab.component';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ChipModel } from '../../../Forms/models/chip.model';
import { ChipContainerComponent } from '../../../Forms/components/ChipContainer/chip-container.component';
import { AutocompleteComponent } from '../../../Forms/components/Autocomplete/autocomplete.component';
import {
    AutocompleteCustomOption,
    AppearCondition
} from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';

import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ListPickerDialogComponent } from '../../../RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { ListPickerService } from '../../../RhpContexts/services/list-picker.service';
import { DistributionPickerService } from '../../../RhpContexts/services/distribution-picker.service';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { FilterGroupModel } from '../../../Filter/models/filter-group.model';

import { SearchModeEnum } from '../../../TargetingShared/enum/search-mode.enum';
import {
    TargetingBatchActionsDialogComponent, TargetingBatchActionsEnum
} from '../../../TargetingShared/components/TargetingBatchActionsDialog/targeting-batch-actions-dialog.component';
import { DistributionService } from '../../../TargetingShared/services/distribution.service';
import { DistributionStatus } from '../../../TargetingShared/enum/distribution-status.enum';
import { DistributionSummaryModel } from '../../../TargetingShared/model/distribution-summary-model';

import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';

import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingSearch } from '../../../../Discover/Targeting/models/targeting-search.model';
import { SourceType } from '../../../../Discover/Targeting/services/source-picker-service';
import { DiscoverRouteTypeEnum } from '../../../../Discover/DiscoverRouter/enums/discover-route-type.enum';
import { DiscoverRouteModel } from '../../../../Discover/DiscoverRouter/models/discover-route.model';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { targetingAddToOptions } from '../../../../Discover/Targeting/models/targeting-add-to-options.model';
import { TargetingAddToTypeEnum } from '../../../../Discover/Targeting/enums/targeting-add-to-type.enum';

import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { EntityTakeoverModel } from '../../models/entity-takeover.model';
import { DistPickerDialogComponent } from '../DistPickerDialog/dist-picker-dialog.component';
import { CommonApiErrorEnum } from '../../../../api/genericApi/enums/common-api-error.enum';

@Component({
    selector: 'agpr-entity-list-tab',
    templateUrl: './entity-list-tab.component.html',
    styleUrls: [
        './entity-list-tab.component.scss',
        '../../../../Discover/Targeting/components/TargetingResults/targeting-results.component.scss' // List picker styling
    ],
    providers: [
        {
            provide: TabComponent,
            useExisting: EntityListTabComponent
        },
        {
            provide: FilterService,
            useExisting: TargetingSearchService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: TargetingSearchService
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: TargetingSearchService
        },
        {
            provide: 'RHPSearchListPickerService',
            useExisting: ListPickerService
        },
        {
            provide: 'RHPSearchDistributionPickerService',
            useExisting: DistributionPickerService
        }
    ],
    standalone: false
})
export class EntityListTabComponent extends TabComponent implements OnInit, OnDestroy {

    @Input() currentRoute: DiscoverRouteModel;
    @Input() targetingList: TargetingList;
    @Input() showActionsButton: boolean = true;

    @Input() heading: string = '';
    @Input() entityMode: EntityType = EntityType.CONTACT;
    @Input() targetingMode: boolean = false;
    @Input() hiddenNoContentMessage: boolean = false;
    @Input() isDynamicEntityType: boolean = false;

    @Output() updateRhpTitle: EventEmitter<string> = new EventEmitter<string>();
    @Output() showRhpTakeOver?: EventEmitter<EntityTakeoverModel> = new EventEmitter<EntityTakeoverModel>();

    declare _header: TemplateRef<any>;

    @ViewChild('tabHeader') set header(value: TemplateRef<any>){
        this._header = value;
    }
    get header(){
        return this._header;
    }

    @ViewChild('tabFooter') declare footer: TemplateRef<any>;
    @ViewChild('listChipsComponent') public listChipsComponent: ChipContainerComponent;
    @ViewChild('listBuilderDropdown') public listBuilderDropdown: AutocompleteComponent;

    id: string = 'entityResultsTab';
    results: TargetingResultModel[];
    selectedResults: TargetingResultModel[] = [];
    targetingSearch: TargetingSearch;
    total: number = 0;
    batchActionsEnum = TargetingBatchActionsEnum;

    readonly actions: MenuItem[] = [
        {
            label: 'Discover.batchActions.addToList',
            value: this.batchActionsEnum.addToList,
            command: () => {
                this.openBatchActions(this.batchActionsEnum.addToList);
            }
        },
        {
            label: 'Discover.batchActions.removeFromList',
            value: this.batchActionsEnum.removeFromList,
            command: () => {
                this.openBatchActions(this.batchActionsEnum.removeFromList);
            }
        },
        {
            label: 'Discover.batchActions.export',
            value: this.batchActionsEnum.export,
            command: () => this.openBatchActions(this.batchActionsEnum.export)
        },
        {
            label: 'Discover.batchActions.exportInteractions',
            value: this.batchActionsEnum.exportInteractions,
            command: () => this.openBatchActions(this.batchActionsEnum.exportInteractions)
        },
        {
            label: 'Discover.batchActions.distribute',
            value: this.batchActionsEnum.distribute,
            command: () => this.openBatchActions(this.batchActionsEnum.distribute)
        }
    ];

    targetingLists: TargetingListList[];
    selectedListsChips: ChipModel[] = [];
    selectedLists: TargetingListList[] = [];
    targetingLists$: Observable<TargetingListList[]>;
    freshCreatedId: number;
    workingListsLocked: boolean = false;

    allSelected: boolean = false;
    chipsVisible = false;
    searchLoading: boolean = true;

    noContentInfo: string;
    quickFilterPlaceholder: string;
    quickFilterAriaLabel: string;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnums = messageSeverityEnum;
    readonly listBuilderOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.listBuilder.createList',
        action: (listName: string) => {
            if (listName.length === 0) {

                return;
            }

            this.targetingListService.isNameDuplicated$(listName).pipe(
                mergeMap((duplicate) => {
                    if (duplicate) {
                        throw new Error(CommonApiErrorEnum.duplicateName);
                    } else {
                        const list = new TargetingList({name: listName});
                        return this.targetingListService.create(list);
                    }
                })
            ).subscribe({
                next: (id) => {
                    this.freshCreatedId = id;
                    this.newlyAddedList();
                },
                error: () => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Discover.dialogs.newList.duplicate',
                    });
                }
            });
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: false
    }];

    readonly targetingAddToOptions = targetingAddToOptions;
    readonly targetingAddToTypes = TargetingAddToTypeEnum;
    addToType: TargetingAddToTypeEnum = TargetingAddToTypeEnum.LIST;
    distributions: DropdownOptionModel<DistributionSummaryModel>[];
    distributions$: Observable<DistributionSummaryModel[]>;
    selectedDistribution: DropdownOptionModel<DistributionSummaryModel>;

    selectedDistributionsChips: ChipModel[] = [];
    selectedDistributions: DistributionSummaryModel[] = [];
    selectedDistributionsTooltip: string;
    workingDistributionsLocked: boolean = false;

    constructor(
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        private coreDialogService: CoreDialogService,
        private targetingSearchesService: TargetingSearchesService,
        private messageService: MessageService,
        protected injector: Injector,
        @Inject('RHPSearchListPickerService') public listPickerService: ListPickerService,
        public distributionService: DistributionService,
        @Inject('RHPSearchDistributionPickerService') public distributionPickerService: DistributionPickerService,
    ) {
        super();
        listPickerService.useLocking = true;
        listPickerService.loadWorkingListsFromSession();
        this.targetingLists$ = this.targetingListService.all$;

        distributionPickerService.useLocking = true;
        distributionPickerService.loadWorkingDistributionsFromSession();
        this.distributions$ = this.distributionService.getDistributionSummaries().pipe(
            filter(x => !!x),
            map(dist => dist.filter(element => element.status === DistributionStatus.draft))
        );

        this.subscriptions.push(
            this.targetingSearchService.getResults().subscribe(results => {
                this.selectNone();
                this.results = results;

                // Determine the contact or outlet icons and tooltips for existing Saved Searches
                if (results !== null && this.isDynamicEntityType) {
                    for(const result of results) {
                        if (result.entityType === EntityType.CONTACT) {
                            this.icon = 'fa-regular fa-duotone fa-users';
                            this.noContentInfo = 'Reports.chartDetailsDialog.noContactsFound';
                            this.quickFilterPlaceholder = 'Discover.targeting.rhp.filter.quickPlaceholder';
                            this.quickFilterAriaLabel = 'Discover.targeting.rhp.filter.contactsLabel';
                            this.updateRhpTitle.emit('Discover.targeting.rhp.contactDetails');
                            break;
                        } else {
                            this.icon = 'fa-regular fa-duotone fa-city';
                            this.noContentInfo = 'Reports.chartDetailsDialog.noOutletsFound';
                            this.quickFilterPlaceholder = 'Reports.chartDetailsDialog.rhp.outletTab.quickFilterPlaceholder';
                            this.quickFilterAriaLabel = 'Reports.chartDetailsDialog.rhp.outletTab.quickFilterAriaLabel';
                            this.updateRhpTitle.emit('Discover.targeting.rhp.outletDetails');
                            break;
                        }
                    }
                }
            }),
            this.targetingSearchService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(chipFilter => {
                    if (chipFilter instanceof FilterGroupModel) {
                        return chipFilter.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return chipFilter.chips && chipFilter.chips.length > 0;
                    }
                });
            }),
            this.targetingSearchesService.currentResource$.subscribe(
                search => {
                    this.selectNone();
                    this.targetingSearch = search;
                }
            ),
            this.listPickerService.getWorkingLists().pipe(
                filter(x => !!x))
                .subscribe(lists => {
                    this.selectedLists = lists;
                    this.selectedListsChips = lists.map(list => ({
                        label: list.name
                    }));
                }),
            this.listPickerService.getWorkingListsLock().subscribe(lock => {
                this.workingListsLocked = lock;
            }),
            this.distributionPickerService.getWorkingDistributions().pipe(
                filter(x => !!x))
                .subscribe(distributions => {
                    this.selectedDistributions = distributions;
                    this.selectedDistributionsChips = distributions.map(list => ({
                        label: list.name
                    }));
                }),
            this.distributionPickerService.getWorkingDistributionsLock().subscribe(lock => {
                this.workingDistributionsLocked = lock;
                if (lock) {
                    this.addToType = TargetingAddToTypeEnum.DISTRIBUTION;
                }
            }),
            this.targetingListService.all$.subscribe(
                (lists) => {
                    this.targetingLists = lists;
                    // remove lists that have been deleted
                    this.selectedLists = this.selectedLists.filter((selectedList) => {
                        return !!this.targetingLists.find(list => selectedList.id === list.id);
                    });

                    this.newlyAddedList();
                }
            ),
            this.targetingSearchService.getPageCriteria().subscribe(() => {
                this.selectNone();
            }),
            this.targetingSearchService.getItemCount().subscribe(x => this.total = x),
            this.targetingSearchService.getSearchLoading().subscribe(x => this.searchLoading = x)
        );
    }


    ngOnInit() {
        if (!this.isDynamicEntityType) {
            if (this.entityMode === EntityType.CONTACT) {
                this.icon = ' fa-users';
                this.noContentInfo = 'Reports.chartDetailsDialog.noContactsFound';
                this.quickFilterPlaceholder = 'Discover.targeting.rhp.filter.quickPlaceholder';
                this.quickFilterAriaLabel = 'Discover.targeting.rhp.filter.contactsLabel';
            } else {
                this.icon = ' fa-city';
                this.noContentInfo = 'Reports.chartDetailsDialog.noOutletsFound';
                this.quickFilterPlaceholder = 'Reports.chartDetailsDialog.rhp.outletTab.quickFilterPlaceholder';
                this.quickFilterAriaLabel = 'Reports.chartDetailsDialog.rhp.outletTab.quickFilterAriaLabel';
            }
        }
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedList = this.targetingLists.find(x => x.id === this.freshCreatedId);
            this.listPickerService.addWorkingList(freshCreatedList);
            this.listPickerService.setWorkingListsLock(true);
            this.freshCreatedId = undefined;
        }
    }

    addToList() {
        this.listPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedListsChips?.length > 0) {
            this.listPickerService.addSelectedEntitiesToWorkingLists();
        } else {
            this.openListPicker();
        }
    }

    clearListChips() {
        this.listChipsComponent.chipPopover.hide();
        this.listPickerService.clearWorkingLists();
    }
    clearDistributionChips() {
        this.listChipsComponent.chipPopover.hide();
        this.distributionPickerService.clearWorkingDistributions();
    }

    updateListBuilder() {
        this.listPickerService.setWorkingLists(this.selectedLists);
        this.selectedListsChips = this.selectedLists.map(workingList => ({
            label: workingList.name
        }));

        this.listPickerService.setWorkingListsLock(true);
    }

    openListPicker(addMode: boolean = true) {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        },
        this.injector);
    }

    openDistPicker(addMode: boolean = true) {
        this.coreDialogService.open(DistPickerDialogComponent, {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        },
        this.injector);
    }

    selectAllInPage(): void {
        // It's null for private contacts view
        if (this.targetingSearch && this.targetingSearch.searchMode === SearchModeEnum.outlet) {
            this.selectedResults = [];
            this.results.forEach(outlet => {
                this.selectedResults.push(outlet);
            });
        } else {
            this.selectedResults = this.results.slice();
        }
    }

    selectNone(): void {
        this.listPickerService.setAllResultsSelected(false);
        this.distributionPickerService.setAllResultsSelected(false);
        this.allSelected = false;
        this.selectedResults = [];
    }

    toggleSelectAll(): void {
        if (this.results.length <= this.selectedResults.length) {
            this.selectNone();
        } else {
            this.selectAllInPage();
        }
    }

    selectAllResults() {
        this.listPickerService.setAllResultsSelected(true, this.total);
        this.distributionPickerService.setAllResultsSelected(true, this.total);
        this.allSelected = true;
    }

    openBatchActions(action?: TargetingBatchActionsEnum): void {
        let sourceType: SourceType;
        let sourceId: number;
        if (this.targetingMode) {
            switch (this.currentRoute.type) {
                case DiscoverRouteTypeEnum.privateContacts:
                    sourceType = SourceType.allPrivateContacts;
                    break;
                case DiscoverRouteTypeEnum.targetingListSearchResult:
                    sourceType = SourceType.lists;
                    sourceId = this.targetingList.id;
                    break;
                default:
                    sourceId = this.targetingSearch.id;
                    sourceType = SourceType.searches;
            }
        }

        this.coreDialogService.open(TargetingBatchActionsDialogComponent, {
            inputs: {
                selectedEntities: this.selectedResults,
                resultsCount: this.allSelected ? this.total : this.selectedResults.length,
                allResultsSelected: this.allSelected,
                initialSourceType: sourceType,
                initialSourceId: sourceId,
                listBuilderLists: this.selectedLists,
                id: 'targetingBatchActionsDialog',
                entityMode: this.entityMode,
                preSelectedBatchActionValue: action
            },
            outputs: {}
        });
    }

    selectTargetingType() {
        this.listPickerService.clearWorkingLists();
        this.distributionPickerService.clearWorkingDistributions();
    }

    addTo() {
        if (this.addToType === this.targetingAddToTypes.LIST) {
            this.addToList();
        } else {
            this.addToDistribution();
        }
    }

    addToDistribution() {
        this.distributionPickerService.setSelectedEntities(this.selectedResults);
        if (this.selectedListsChips?.length > 0) {
            this.distributionPickerService.addSelectedEntitiesToWorkingDists();
        } else {
            this.openDistPicker();
        }
    }

    updateDistributionBuilder() {
        this.distributionPickerService.setWorkingDistributions(this.selectedDistributions);
        this.selectedDistributionsChips = this.selectedDistributions.map(workingDistribution => ({
            label: workingDistribution.name
        }));
        this.distributionPickerService.setWorkingDistributionsLock(true);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
        this.targetingSearchService.registerPagingValidator(null);
    }
}
